import { useState } from 'react';

import { filterByQuery } from './triageHelpers';
import { DashboardRow, formatDealerDateTime } from './triageDeskShared';
export function Cell({ value, dim = false }: { value: string | null | undefined; dim?: boolean }) {
  if (!value || value.trim().length === 0) {
    return (
      <span
        className="inline-flex items-center text-xs italic text-gray-400 border border-dashed border-gray-300 rounded px-1.5 py-0.5"
        title="Not yet verified by Ananya"
      >
        unverified
      </span>
    );
  }
  return <span className={dim ? 'text-gray-500' : 'text-gray-900'}>{value}</span>;
}

export function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      type="button"
      onClick={copy}
      className={`text-xs font-medium rounded px-2 py-0.5 border transition-colors ${copied ? 'text-green-700 border-green-400 bg-green-50' : 'text-gray-700 border-gray-300 hover:bg-gray-100'}`}
      title="Copy the full lead summary to clipboard"
    >
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  );
}

export function VerifiedBadge({ status }: { status: 'verified' | 'unverified' }) {
  if (status === 'verified') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded">
        <span className="w-1.5 h-1.5 bg-green-500 rounded-full" /> verified
      </span>
    );
  }
  return null;
}

export function DealerAssignedTable({
  rows,
  loading,
  pendingIds,
  onReassign,
  onMarkSent,
}: {
  rows: DashboardRow[];
  loading: boolean;
  pendingIds: ReadonlySet<string>;
  onReassign: (row: DashboardRow) => void;
  onMarkSent: (row: DashboardRow) => void;
}) {
  return (
    <table className="min-w-full text-sm">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          {['Lead', 'Requirement', 'Dealer', 'Manager', 'Human owner', 'Assigned', 'Dealer status', 'Action'].map((heading) => (
            <th key={heading} className="text-left px-3 py-2 font-semibold text-gray-700">{heading}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {loading && Array.from({ length: 4 }).map((_, index) => (
          <tr key={index} className="border-b border-gray-100 animate-pulse">
            {Array.from({ length: 8 }).map((__, cell) => (
              <td key={cell} className="px-3 py-3"><div className="h-3 bg-gray-100 rounded w-20" /></td>
            ))}
          </tr>
        ))}
        {!loading && rows.length === 0 && (
          <tr>
            <td colSpan={8} className="px-3 py-12 text-center text-sm text-gray-500">
              No dealer-assigned leads found. Adjust the filters above or check back later.
            </td>
          </tr>
        )}
        {!loading && rows.map((row) => {
          const pending = pendingIds.has(row.id);
          const sent = Boolean(row.dealerNotifiedAt);
          const requirementMeta = [row.applicationType, row.tier].filter(Boolean).join(' · ');
          return (
            <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-3 py-2">
                <div className="font-medium text-gray-900"><Cell value={row.name} /></div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {[row.phone, row.location].filter(Boolean).join(' · ') || '—'}
                </div>
              </td>
              <td className="px-3 py-2">
                <div><Cell value={row.requirement} /></div>
                {requirementMeta ? <div className="text-xs text-gray-500 mt-0.5">{requirementMeta}</div> : null}
              </td>
              <td className="px-3 py-2">
                <span className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5 whitespace-nowrap">
                  <span aria-hidden>●</span><Cell value={row.assignedDealerName} />
                </span>
              </td>
              <td className="px-3 py-2"><Cell value={row.assignedDealerManagerName} /></td>
              <td className="px-3 py-2"><Cell value={row.assignedToName} /></td>
              <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                {row.dealerAssignedAt ? (
                  <time dateTime={row.dealerAssignedAt}>{formatDealerDateTime(row.dealerAssignedAt)}</time>
                ) : <Cell value={null} />}
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                {sent ? (
                  <>
                    <span className="inline-flex items-center text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded px-2 py-0.5">
                      <span aria-hidden>✓&nbsp;</span>Sent · <time dateTime={row.dealerNotifiedAt ?? undefined}>{formatDealerDateTime(row.dealerNotifiedAt)}</time>
                    </span>
                    <div className="text-xs text-gray-500 mt-0.5">
                      by {row.dealerNotifiedByName || (row.dealerNotifiedByUserId ? 'another user' : 'actor unavailable')}
                    </div>
                  </>
                ) : (
                  <span className="inline-flex items-center text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                    Not sent
                  </span>
                )}
              </td>
              <td className="px-3 py-2">
                <div className="flex flex-col items-start gap-1.5">
                  <button
                    type="button"
                    onClick={() => onReassign(row)}
                    disabled={pending}
                    className="text-xs border border-gray-300 rounded px-2 py-1 bg-white hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none whitespace-nowrap"
                  >
                    Reassign ▾
                  </button>
                  <button
                    type="button"
                    onClick={() => onMarkSent(row)}
                    disabled={sent || pending}
                    className={`text-xs border rounded px-2 py-1 font-medium focus:outline-none focus:ring-1 disabled:opacity-50 disabled:pointer-events-none whitespace-nowrap ${
                      sent
                        ? 'border-gray-300 bg-white text-gray-500'
                        : 'border-green-600 bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
                    }`}
                  >
                    {sent ? 'Sent' : pending ? 'Marking…' : <><span aria-hidden>✓&nbsp;</span>Mark as sent</>}
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function SearchCombo({
  label,
  value,
  options,
  placeholder,
  disabled,
  onSelect,
}: {
  label: string;
  value: string;
  options: string[];
  placeholder?: string;
  disabled?: boolean;
  onSelect: (option: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const filtered = filterByQuery(options, query);
  return (
    <label className="text-sm block relative">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-label={label}
        value={open ? query : value}
        placeholder={value && !open ? value : placeholder}
        disabled={disabled}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={(e) => setQuery(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
      />
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-400">No matches</div>
          ) : (
            filtered.map((o) => (
              <button
                key={o}
                type="button"
                // mousedown (not click) so the selection lands before onBlur closes the list.
                onMouseDown={(e) => { e.preventDefault(); onSelect(o); setOpen(false); }}
                className="block w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50"
              >
                {o}
              </button>
            ))
          )}
        </div>
      )}
    </label>
  );
}
